import { useCallback, useMemo } from 'react'
import { $getRoot, $getSelection, $isRangeSelection, $selectAll, $setSelection, LexicalEditor } from 'lexical'
import { ResultOption } from './ResultOptionsButton'
import { nanoid } from 'nanoid'
import { $wrapSelectionInMarkNode } from '@lexical/mark'
import { $getHtmlContent } from '@lexical/clipboard'
import { SidebarTitle, SidebarType } from '../../../Sidebar/models'
import { AiResult } from '../../../../../../../models/AiResult'
import { $generateHtmlFromNodes } from '@lexical/html'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { selectAiFeatures } from '../../../../../../../store/meetingSummary/aiFeatures'
import { displaySidebar, showToast } from '../../../../../../../store/meetingSummary/display'
import {
  selectMeetingNote,
  setCommentedAiResultId,
  setMeetingNoteChangedExternally,
  setNewComment,
  setResultExplainerAiFeatureId,
  setResultExplainerResult,
} from '../../../../../../../store/meetingSummary/meetingSupport'
import { useUpdateMeetingNote } from '../../../hooks/api'
import { addHtmlContentToState, parseIntoHeadlessEditor } from '../../Lexical/utils'
import { useZocksAuth } from '../../../../../../../components/ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { getAIFeatureAndResultTrackingDetails } from '../../../../tracking'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { truncateQuote } from '../../Lexical/Plugins/CommentPlugin/CommentPlugin'
import { useEffectiveConfig } from '../../../../../../../common/useEffectiveConfig'
import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../../../../../common/copy'

export const useDefaultResultOptions = (
  editor: LexicalEditor | null,
  aiFeature: AiFeature,
  aiResult: AiResult,
  path: string,
  basePath: string = '$',
): ResultOption[] => {
  const copyResultOption = useCopyResultOption(editor, aiFeature, aiResult)
  const notepadResultOption = useNotepadResultOption(editor, aiFeature, aiResult)
  const commentResultOption = useCommentResultOption(editor, aiFeature, aiResult, path, basePath)

  return [copyResultOption, notepadResultOption, commentResultOption]
}

export const useCopyResultOption = (editor: LexicalEditor | null, aiFeature: AiFeature, aiResult: AiResult): ResultOption => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const _handleCopyClicked = useCallback(async () => {
    if (!editor || accountIsFetching) {
      return
    }
    editor.update(() => {
      const textContent = $getRoot().getTextContent()
      $selectAll()
      const htmlContent = $getHtmlContent(editor)

      const copiedResult = { [TEXT_TYPE]: textContent, [HTML_TYPE]: htmlContent }
      const copyContent = extendCopyContentWithComplianceMessage(copiedResult, copyComplianceMessage)

      writeCopyContentToClipboard(copyContent)

      $setSelection(null)
    })
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }, [accountIsFetching, copyComplianceMessage, dispatch, editor])

  const handleCopyClicked = tracked(_handleCopyClicked, {
    eventName: TrackEvent.AI_RESULT_COPIED,
    eventDetails: {
      ...getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
    },
  })

  return { selectOption: { id: 'copy', label: 'Copy' }, icon: 'bi-copy', handleSelected: handleCopyClicked }
}

export const useNotepadResultOption = (editor: LexicalEditor | null, aiFeature: AiFeature, aiResult: AiResult): ResultOption => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const meetingNote = useAppSelector(selectMeetingNote)

  const updateMeetingNote = useUpdateMeetingNote()

  const { hasScope } = useZocksAuth()
  const hasMeetingNoteScope = hasScope('**', 'meeting_note:update', ['meeting_note:admin'])

  const _handleNotepadClicked = async () => {
    if (!editor || !hasMeetingNoteScope) {
      return
    }
    const htmlContent = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    const headlessEditor = parseIntoHeadlessEditor(meetingNote)
    addHtmlContentToState(headlessEditor, htmlContent)
    const newMeetingNote = JSON.stringify(headlessEditor.getEditorState().toJSON())
    await updateMeetingNote(newMeetingNote)
    dispatch(setMeetingNoteChangedExternally(true))
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Added to notepad!' }))
  }

  const handleNotepadClicked = tracked(_handleNotepadClicked, {
    eventName: TrackEvent.AI_RESULT_ADDED_TO_NOTEPAD,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  return {
    selectOption: { id: 'notepad', label: 'Send to Notepad', disabled: !hasMeetingNoteScope },
    icon: 'bi-journal-text',
    handleSelected: handleNotepadClicked,
  }
}

export const useCommentResultOption = (
  editor: LexicalEditor | null,
  aiFeature: AiFeature,
  aiResult: AiResult,
  path: string,
  basePath: string,
): ResultOption => {
  const dispatch = useAppDispatch()

  const aiFeatures = useAppSelector(selectAiFeatures)

  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const hasCommentScope = hasScope('**', 'comment:create', ['comment:admin'])

  const { id: aiResultId, aiFeatureId } = aiResult

  const sidebarTitle = useMemo(() => {
    const aiFeature = aiFeatures.find((f) => f.id === aiFeatureId)
    if (aiFeature?.name) {
      return `${SidebarTitle.COMMENTS} - ${aiFeature.name}`
    }
    return SidebarTitle.COMMENTS
  }, [aiFeatureId, aiFeatures])

  const _handleCommentClicked = () => {
    if (!editor || !hasCommentScope) {
      return
    }
    editor.update(() => {
      $selectAll()

      const selection = $getSelection()
      if (!$isRangeSelection(selection)) {
        return
      }

      const originalQuote = editor.getEditorState().read(() => selection.getTextContent())
      const quote = truncateQuote(originalQuote)
      const isBackward = selection.isBackward()
      const markId = nanoid()
      $wrapSelectionInMarkNode(selection, isBackward, markId)
      const newComment = { markId, quote, aiResultId, basePath, path }
      dispatch(setNewComment(newComment))
      dispatch(setCommentedAiResultId(aiResultId))
      dispatch(displaySidebar({ type: SidebarType.COMMENTS, title: sidebarTitle, icon: 'bi-chat-square-text' }))
    })
  }

  const handleCommentClicked = tracked(_handleCommentClicked, {
    eventName: TrackEvent.AI_RESULT_COMMENTED,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  return {
    selectOption: { id: 'comment', label: 'Comment', disabled: !hasCommentScope },
    icon: 'bi-chat-square-text',
    handleSelected: handleCommentClicked,
  }
}

export const useResultExplainerResultOption = (editor: LexicalEditor | null, aiFeature: AiFeature, aiResult: AiResult): ResultOption => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const { id: aiFeatureId } = aiFeature

  const { hasScope } = useZocksAuth()
  const hasExecuteScope = hasScope('**', 'aifeature:execute', ['aifeature:admin'])

  const _handleResultExplainerClicked = () => {
    if (!editor || !hasExecuteScope) {
      return
    }
    const textContent = editor.getEditorState().read(() => $getRoot().getTextContent())
    dispatch(setResultExplainerAiFeatureId(aiFeatureId))
    dispatch(setResultExplainerResult(textContent))
    dispatch(displaySidebar({ type: SidebarType.RESULT_EXPLAINER, title: SidebarTitle.RESULT_EXPLAINER, icon: 'bi-journal-text' }))
  }

  const handleResultExplainerClicked = tracked(_handleResultExplainerClicked, {
    eventName: TrackEvent.AI_RESULT_EXPLAINED,
    eventDetails: getAIFeatureAndResultTrackingDetails(aiFeature, aiResult),
  })

  return {
    selectOption: { id: 'resultExplainer', label: 'Result explainer', disabled: !hasExecuteScope },
    icon: 'bi-chat-square-quote',
    handleSelected: handleResultExplainerClicked,
  }
}
